import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import Dropdown from "layout/atoms/Dropdown";
import Form from "layout/atoms/Form";
import Icon from "layout/atoms/Icon";
import InputText from "layout/atoms/InputText";
import Loader from "layout/atoms/Loader";
import Modal from "layout/atoms/Modal";
import Status from "layout/molecules/Status";
import VotesCounter from "layout/molecules/VotesCounter";
import Comments from "layout/organisms/IdeaCard/Comments";
import {closeModal, openModal} from "store/modalsSlice";
import {
    changeIdeaStatus,
    deleteIdea,
    downvoteIdea,
    editIdea,
    fetchIdea as fetchIdeaThunk,
    selectIdeaById,
    selectIdeasStatus,
    upvoteIdea,
} from "store/currentBoardSlice";

import {ReactComponent as EditSvg} from "assets/icons/edit1.svg";
import {ReactComponent as EyeSvg} from "assets/icons/eye.svg";
import {ReactComponent as TrashSvg} from "assets/icons/trash.svg";
import {Main, ModalButtons, ModalTop} from "layout/organisms/IdeaCard/styles";

const IdeaModal = ({ ideaId, boardId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("translation", {
    keyPrefix: "boardsDetails.ideaCard",
  });
  const { t: tGeneral } = useTranslation("translation");

  const idea = useSelector((state) => selectIdeaById(state, ideaId));
  const { editIdea: editStatus, fetchIdea: fetchStatus } = useSelector(selectIdeasStatus);
  const [editMode, setEditMode] = useState(false);
  const isMountedRef = useRef(true);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    dispatch(fetchIdeaThunk({ ideaId, boardId }));
    return () => {
      isMountedRef.current = false;
    };
  }, [ideaId]);

  useEffect(() => {
    if (idea?.title != null) {
      reset({ title: idea.title, description: idea.description });
    }
  }, [idea?.title, idea?.description]);

  const handleClose = () => dispatch(closeModal("ideaModal"));

  const handleEditSave = (data) => {
    dispatch(editIdea({ data, ideaId, boardId }))
      .unwrap()
      .then(() => setEditMode(false));
  };

  const handleDelete = () => {
    handleClose();
    dispatch(deleteIdea({ ideaId, boardId }));
  };

  const increaseCommentCount = () => {};

  const OptionsButton = () => (
    <Dropdown closeOnClick>
      <Button
        size="md"
        color="white"
        disabled={editMode}
        onClick={() => setEditMode(true)}
      >
        <Icon src={EditSvg} />
        <p>{t("edit")}</p>
      </Button>
      <Button size="md" color="white" onClick={handleDelete}>
        <Icon src={TrashSvg} />
        <p>{t("remove")}</p>
      </Button>
    </Dropdown>
  );

  return (
    <Modal open onClose={handleClose}>
      <ContentBox>
        {fetchStatus === "loading" || !idea ? (
          <Loader />
        ) : (<>
        <ModalButtons>{idea?.canEdit && <OptionsButton />}</ModalButtons>
        <ModalTop>
          <div>
            <VotesCounter
              votesNumber={idea.voteCount}
              isVotedByMe={idea.isVotedByMe}
              onVoteClick={
                idea.isVotedByMe
                  ? () => dispatch(downvoteIdea({ ideaId, boardId }))
                  : () => dispatch(upvoteIdea({ ideaId, boardId }))
              }
            />
          </div>
          <Main inModal>
            {editMode && (
              <Form onSubmit={handleSubmit(handleEditSave)}>
                <InputText
                  type="text"
                  name="title"
                  label={t("fields.title", { defaultValue: "Title" })}
                  register={register}
                  errors={errors.title}
                  validation={{ required: true, minLength: 6, maxLength: 100 }}
                />
                <InputText
                  type="textarea"
                  name="description"
                  label={t("fields.description", { defaultValue: "Description" })}
                  register={register}
                  errors={errors.description}
                  validation={{ maxLength: { value: 10000, message: tGeneral("formErrors.tooLong") } }}
                />
                <div>
                  <Button type="button" size="lg" color="white" onClick={() => setEditMode(false)} disabled={editStatus === "loading"}>
                    {t("cancel", { defaultValue: "Cancel" })}
                  </Button>
                  <Button type="submit" size="lg" color="primary" loading={editStatus === "loading"}>
                    {t("save", { defaultValue: "Save" })}
                  </Button>
                </div>
              </Form>
            )}
            {idea.title && !editMode && <h4>{idea.title}</h4>}
            {idea.description && !editMode && <p>{idea.description}</p>}
            {idea.author && !editMode && (
              <p>
                {t("addedBy")} <b>{idea.author.displayName}</b>
              </p>
            )}
            {idea.status && !editMode && (
              <Status
                statusName={idea.status}
                canUpdateStatus={idea.canUpdateStatus}
                onStatusClick={(status) =>
                  dispatch(changeIdeaStatus({ status, ideaId, boardId }))
                }
                onDeclineClick={() =>
                  dispatch(openModal({ name: "declineIdeaModal", data: { ideaId, boardId } }))
                }
              />
            )}
            {idea.canEdit && idea.voteCount > 0 && !editMode && (
              <div style={{ marginTop: "12px" }}>
                <Button
                  size="sm"
                  color="white"
                  onClick={() => dispatch(openModal({ name: "votersModal", data: { ideaId, boardId } }))}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  <Icon src={EyeSvg} />
                  <p>{t("lookupVoters", { defaultValue: "View voters" })}</p>
                </Button>
              </div>
            )}
            {idea.status === "DECLINED" && idea.declineDetails && !editMode && (
              <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "var(--white2)", borderRadius: "4px" }}>
                <h5 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "600", color: "var(--gray)" }}>
                  {t("declineInfo.title", { defaultValue: "Decline Information" })}
                </h5>
                <p style={{ margin: "0 0 4px 0", fontSize: "13px" }}>
                  <strong>{t("declineInfo.reason", { defaultValue: "Reason" })}:</strong>{" "}
                  {tGeneral(`boardsDetails.declineModal.reasons.${idea.declineDetails.reason}`, { defaultValue: idea.declineDetails.reason })}
                </p>
                {idea.declineDetails.comment && (
                  <p style={{ margin: "0 0 4px 0", fontSize: "13px" }}>
                    <strong>{t("declineInfo.comment", { defaultValue: "Comment" })}:</strong>{" "}
                    {idea.declineDetails.comment}
                  </p>
                )}
              </div>
            )}
          </Main>
        </ModalTop>
        <Comments
          boardId={boardId}
          ideaId={ideaId}
          commentCount={idea?.commentCount}
          onCommentAdded={increaseCommentCount}
        />
        </>)}
      </ContentBox>
    </Modal>
  );
};

export default IdeaModal;
